import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { WDSButton } from 'wanderu-components';
import { formatAddressValue } from 'wanderu-utils';

import googleImgPath from 'wanderu-assets/images/powered_by_google_on_white.png';
import googleImgPath2x from 'wanderu-assets/images/powered_by_google_on_white_hdpi.png';

import { getIsSafari } from './utils';

import styles from './Options.scss';

function makeSubstrings(text, match) {
    const matchSpan = (str, i = 0) => (
        <span className={styles.match} key={`match-${i}`}>{str}</span>
    );

    const textSpan = (str, i = 0) => (
        <span key={i}>{str}</span>
    );

    if (!text) {
        return null;
    }

    if (!match || match.length === 0) {
        return textSpan(text);
    }

    let formattedMatch = match.toLowerCase();

    if (text.toLowerCase() === formattedMatch) {
        return matchSpan(text);
    }

    let substrings = [];
    let num = 0;
    let curr = text;

    while (curr.length > 0) {
        const matchIndex = curr.toLowerCase().indexOf(formattedMatch);

        if (matchIndex === -1) {
            substrings.push(textSpan(curr.substring(0), num));
            break;
        }

        if (matchIndex !== 0) {
            const textSubstring = curr.substring(0, matchIndex);
            substrings.push(textSpan(textSubstring, num));
        }

        const matchSubstring = curr.substring(matchIndex, matchIndex + formattedMatch.length);
        substrings.push(matchSpan(matchSubstring, num));
        curr = curr.substring(matchIndex + formattedMatch.length);
        num += 1;
    }

    return substrings;
}

function Options(props) {
    const {
        className,
        'data-id': dataId,
        highlightClass,
        links,
        match,
        onMouseEnter,
        onMouseLeave,
        optionContainerClass,
        options,
        position,
        poweredByGoogle,
        showOptions,
        showOptionsCaret,
        substringMatch,
    } = props;

    function renderGoogleAttribution() {
        return (
            <div className={styles.poweredByGoogle}>
                <img
                    alt="Powered by Google"
                    src={googleImgPath}
                    srcSet={`${googleImgPath2x} 2x`}
                />
            </div>
        );
    }

    if (!showOptions) {
        return null;
    }

    const isSafari = getIsSafari();

    return (
        <ul
            className={classNames(
                className,
                optionContainerClass,
                styles.dropdown,
                { [styles.showCaret]: showOptionsCaret },
                { [styles.greyCaret]: showOptionsCaret && position === 0 },
            )}
            data-id={dataId}
        >
            {options.map((option, i) => (
                <li
                    aria-current={i === position}
                    className={classNames(
                        styles.optionContainer,
                        {
                            [styles.highlighted]: i === position,
                            [highlightClass]: i === position,
                        },
                    )}
                    key={i}
                    onMouseEnter={() => onMouseEnter(i)}
                    onMouseLeave={onMouseLeave}
                    onTouchStart={() => onMouseEnter(i)}
                >
                    <span
                        className={styles.option}
                    >
                        {!isSafari && substringMatch // [MAIN-7036] Safari browser auto-translate is crashing when a substring is deleted, so we are disabling
                            ? makeSubstrings(formatAddressValue(option.text), match)
                            : <span>{formatAddressValue(option.text)}</span>}
                    </span>
                </li>
            ))}
            {poweredByGoogle && renderGoogleAttribution()}

            {links && <div className={styles.linksContainer}>
                {links.map(({ Icon, onClick, text }) => (
                    <WDSButton className={styles.linkButton} key={text} onClick={onClick} variant="unstyled" fullWidth>
                        <Icon /> {text}
                    </WDSButton>
                ))}
            </div>}
        </ul>
    );
}

Options.propTypes = {
    className: PropTypes.string,
    'data-id': PropTypes.string,
    highlightClass: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            Icon: PropTypes.elementType,
            onClick: PropTypes.func,
            text: PropTypes.string,
        })
    ),
    match: PropTypes.string,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    optionContainerClass: PropTypes.string,
    options: (props, propName, componentName) => {
        const options = props[propName];
        const textKeyMessage = 'Options must be an array of objects with a `text` key of type string.';
        if (!options || !Array.isArray(options)) {
            return new Error(
                `Required prop \`${propName}\` not supplied to \`${componentName}\`. ${textKeyMessage}`,
            );
        }
        const allHaveTextKey = options.every((option) => Object.prototype.hasOwnProperty.call(option, 'text'));
        if (options.length > 0 && !allHaveTextKey) {
            return new Error(
                `Invalid prop \`${propName}\` supplied to \`${componentName}\`. ${textKeyMessage}`,
            );
        }

        return undefined;
    },
    position: PropTypes.number,
    poweredByGoogle: PropTypes.bool,
    showOptions: PropTypes.bool,
    showOptionsCaret: PropTypes.bool,
    substringMatch: PropTypes.bool,
};

Options.defaultProps = {
    className: '',
    'data-id': '',
    highlightClass: '',
    match: '',
    onMouseEnter() {},
    onMouseLeave() {},
    optionContainerClass: '',
    options: [],
    position: 0,
    poweredByGoogle: false,
    showOptions: false,
    showOptionsCaret: false,
    substringMatch: true,
};

export default Options;
